// GoalCreateModal — template picker for standing goals
// Shows 6 template cards; clicking one creates the goal+schedule atomically.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../Modal/Modal';
import { FormActions } from '../ui';
import { listTemplates, createGoalFromTemplate, formatInterval, type GoalTemplate } from '../../api/goals';
import styles from './GoalCreateModal.module.css';

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

function TemplateCard({
  tmpl,
  selected,
  onClick,
}: {
  tmpl: GoalTemplate;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`${styles.tmplCard} ${selected ? styles.tmplCardSelected : ''}`}
      onClick={onClick}
    >
      <span className={styles.tmplIcon}>{tmpl.icon}</span>
      <span className={styles.tmplName}>{tmpl.name}</span>
      <span className={styles.tmplDesc}>{tmpl.description}</span>
      <span className={styles.tmplSchedule}>{formatInterval(tmpl)}</span>
    </button>
  );
}

export default function GoalCreateModal({ onClose, onCreated }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['goal-templates'],
    queryFn: listTemplates,
    staleTime: 5 * 60_000,
  });

  async function handleCreate() {
    if (!selected || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await createGoalFromTemplate(selected);
      onCreated();
    } catch (e) {
      setError((e as Error).message || 'Failed to create goal.');
      setSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="New Standing Goal"
      size="medium"
      footer={
        <FormActions
          onCancel={onClose}
          submitLabel={submitting ? 'Creating…' : 'Create Goal'}
          submitType="button"
          onSubmit={handleCreate}
          isSubmitting={submitting}
          submitDisabled={!selected}
        />
      }
    >
      <p className={styles.modalSubtitle}>
        Pick a template — the agent will run it on a recurring schedule and report back here.
      </p>

      {isLoading && <p className={styles.loading}>Loading templates…</p>}

      {templates && (
        <div className={styles.tmplGrid}>
          {templates.map((t) => (
            <TemplateCard key={t.key} tmpl={t} selected={selected === t.key} onClick={() => setSelected(t.key)} />
          ))}
        </div>
      )}

      {error && <p className={styles.errorMsg}>{error}</p>}
    </Modal>
  );
}
